import React, { useState, useEffect } from 'react';
import { Mail, User, Calendar, MessageSquare, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const ContactsManagement = ({ apiKey }) => {
  const [contacts, setContacts] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [activeTab, setActiveTab] = useState('contacts'); // 'contacts' or 'newsletter'
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch contacts from backend
      const contactsRes = await fetch(`${apiUrl}/api/contacts`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (contactsRes.ok) {
        const contactsData = await contactsRes.json();
        setContacts(contactsData);
      }

      // Fetch newsletter subscribers
      const newslettersRes = await fetch(`${apiUrl}/api/newsletter`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (newslettersRes.ok) {
        const newslettersData = await newslettersRes.json();
        setNewsletters(newslettersData.map(sub => ({
          ...sub,
          created_at: sub.subscribed_at
        })));
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = (data, filename) => {
    const csv = data.map(row => Object.values(row).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des Contacts</h2>
        <p className="text-gray-600 mt-1">
          {contacts.length} demande(s) de contact • {newsletters.length} abonné(s) newsletter
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('contacts')}
              className={`px-6 py-4 font-semibold transition-colors ${
                activeTab === 'contacts'
                  ? 'border-b-2 border-amber-600 text-amber-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Mail className="h-5 w-5 inline mr-2" />
              Demandes de contact ({contacts.length})
            </button>
            <button
              onClick={() => setActiveTab('newsletter')}
              className={`px-6 py-4 font-semibold transition-colors ${
                activeTab === 'newsletter'
                  ? 'border-b-2 border-amber-600 text-amber-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Newsletter ({newsletters.length})
            </button>
          </div>
        </div>
      </div>

      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <div>
          <div className="mb-4 flex justify-end">
            <Button
              onClick={() => exportToCSV(contacts, 'contacts.csv')}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter en CSV
            </Button>
          </div>

          {contacts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-600">Aucune demande de contact</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {contacts.map((contact) => (
                <Card key={contact.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{contact.subject}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {contact.name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            {contact.email}
                          </span>
                          {contact.phone && (
                            <span>📱 {contact.phone}</span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(contact.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-start gap-2 text-gray-700">
                        <MessageSquare className="h-5 w-5 text-gray-400 mt-1" />
                        <p className="flex-1">{contact.message}</p>
                      </div>
                    </div>

                    <div className="mt-4 flex gap-2">
                      <Button
                        onClick={() => window.open(`mailto:${contact.email}`, '_blank')}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        Répondre par Email
                      </Button>
                      <Button
                        onClick={() => window.open(`https://wa.me/${contact.phone?.replace(/\D/g, '')}`, '_blank')}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        Répondre sur WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Newsletter Tab */}
      {activeTab === 'newsletter' && (
        <div>
          <div className="mb-4 flex justify-end">
            <Button
              onClick={() => exportToCSV(newsletters, 'newsletter.csv')}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter en CSV
            </Button>
          </div>

          {newsletters.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-600">Aucun abonné newsletter</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {newsletters.map((subscriber) => (
                    <div key={subscriber.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-amber-600" />
                        <span className="font-medium text-gray-900">{subscriber.email}</span>
                      </div>
                      <span className="text-sm text-gray-500">{formatDate(subscriber.created_at)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Info Box */}
      <Card className="mt-6 bg-amber-50 border-amber-200">
        <CardContent className="p-6">
          <h3 className="font-bold text-amber-900 mb-3">📧 Astuce</h3>
          <p className="text-sm text-amber-800">
            Répondez rapidement à vos demandes de contact pour augmenter vos chances de conversion. Un délai de réponse de moins de 24h améliore significativement votre taux de conversion !
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactsManagement;
